import { useState } from "react";
import { LayoutGrid, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * LiveRoomHostStageControls — host-only Layout dropdown.
 *
 * Renders a compact pill in the top-right of the live room that lets the host
 * choose between Auto (adaptive, Stage 4a default) and a fixed 1/2/4/6/9 grid.
 * The chosen layout is persisted on the stream doc via `POST /live/{id}/stage`
 * and picked up by every viewer through the existing 6s freshStream poll.
 *
 * Pin & Spotlight are triggered by tapping a tile inside the stage itself
 * (see `LiveRoomStage`) — that keeps the header uncluttered on mobile.
 * A tiny "Clear pin/spotlight" chip appears here when either is active so the
 * host can undo without hunting for the right tile.
 */

const OPTIONS = [
  { key: "auto", label: "Auto", detail: "Adaptive layout" },
  { key: "1",    label: "1-box", detail: "Solo full-screen" },
  { key: "2",    label: "2-box", detail: "Side-by-side" },
  { key: "4",    label: "4-box", detail: "2×2 grid" },
  { key: "6",    label: "6-box", detail: "3×2 grid · Panda+", premium: true },
  { key: "9",    label: "9-box", detail: "3×3 grid · Panda+", premium: true },
];

// `premium` = host is Panda+ or the owner → unlocks the 6/9-box layouts (9 seats).
export default function LiveRoomHostStageControls({ streamId, stage, isHost, onStageChange, premium = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!isHost) return null;

  const options = OPTIONS.filter((o) => !o.premium || premium);
  const currentLayout = stage?.layout || "auto";
  const currentLabel = OPTIONS.find((o) => o.key === currentLayout)?.label || "Auto";
  const spotlightActive = !!stage?.spotlight_id;
  const pinActive = !!stage?.pinned_id;

  const patch = async (body) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/live/${streamId}/stage`, body);
      onStageChange?.(data?.stage);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the stage.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hidden lg:flex absolute top-2 right-[7.5rem] z-30 flex-col items-end gap-2">
      <div className="relative">
        <button
          type="button"
          data-testid="live-host-layout-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/40 backdrop-blur border border-white/10 text-white/80 text-[10px] uppercase tracking-widest font-medium hover:border-white/25 transition-colors"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <LayoutGrid size={11} />
          <span data-testid="live-host-layout-current">{currentLabel}</span>
          <ChevronDown size={10} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div
            className="absolute right-0 mt-2 w-56 rounded-2xl bg-zinc-950/95 border border-white/10 backdrop-blur-md shadow-2xl p-1.5 z-40"
            data-testid="live-host-layout-menu"
            role="menu"
          >
            {options.map((o) => {
              const isCurrent = o.key === currentLayout;
              return (
                <button
                  key={o.key}
                  type="button"
                  data-testid={`live-host-layout-option-${o.key}`}
                  onClick={() => {
                    patch({ layout: o.key });
                    setOpen(false);
                  }}
                  disabled={busy}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left text-sm ${
                    isCurrent
                      ? "bg-rose-500/20 text-rose-100 border border-rose-500/40"
                      : "text-white/85 hover:bg-white/5 border border-transparent"
                  }`}
                  role="menuitemradio"
                  aria-checked={isCurrent}
                >
                  <span className="font-semibold">{o.label}</span>
                  <span className="text-[11px] text-white/50">{o.detail}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {(spotlightActive || pinActive) && (
        <button
          type="button"
          data-testid="live-host-stage-clear"
          onClick={() => patch({ pinned_id: "", spotlight_id: "" })}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur border border-white/15 text-white text-[11px] hover:border-white/40"
        >
          <X size={12} />
          {spotlightActive ? "Clear spotlight" : "Clear pin"}
        </button>
      )}
    </div>
  );
}
